import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { AnimateIn } from '../../../components/animate-in';
import { RouteError } from '../../../components/route-container/route-error';
import { FastractPendingData } from '../../../types';
import { captureError } from '../../../utils/capture-error';
import { isAgt } from '../../../utils/is-agent';
import { AgentApproval } from './agent-approval';
import { ManageApprovals } from './manage-approvals';

export function FastractApprovals(props: {
  enabled: boolean;
  isContAuth: boolean;
  AgtNo: string | undefined;
}) {
  // Props
  const { enabled, isContAuth, AgtNo } = props;
  // State
  const [approval, setApproval] = React.useState<FastractPendingData>();
  const [errMsg, setErrMsg] = React.useState('');

  // Query - Fastract Approvals
  const path = '/agent/fastract/pending';
  const query = useQuery({
    enabled,
    queryKey: [path, { AgtNo }],
    queryFn: async () => {
      if (isAgt(AgtNo)) {
        const response: {
          data: FastractPendingData[];
        } = await API.post('ContractingAPI', path, {
          body: { AgtNo },
        });

        if (response.data.length) {
          return response.data;
        }
      }

      return [];
    },
    onError: (error) => {
      if (error instanceof Error) {
        if (
          // @ts-ignore
          error?.response?.data?.error &&
          // @ts-ignore
          error?.response?.data?.status === 'forbidden'
        ) {
          // @ts-ignore
          const newError = error.response.data.error as string;
          setErrMsg(newError);
        }
      }
      captureError({ data: { error } });
    },
  });

  if (Boolean(query.isLoading && query.fetchStatus !== 'idle')) {
    return <LinearProgress color="info" />;
  } else if (query.isError && errMsg) {
    return (
      <Container maxWidth="md" sx={{ pt: 2 }}>
        <Alert severity="warning">
          You are not able to view{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            Fastract Approvals
          </Box>{' '}
          for the impersonated Agent{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {props.AgtNo}
          </Box>
          .
        </Alert>
      </Container>
    );
  } else if (query.isError) {
    return <RouteError />;
  } else {
    return (
      <AnimateIn to="top">
        <Container maxWidth="md" sx={{ pt: 2, pb: 16 }}>
          <Stack spacing={2}>
            <ManageApprovals
              AgtNo={AgtNo}
              data={query.data || []}
              onSelect={(newApproval) => setApproval(newApproval)}
            />
          </Stack>

          <AgentApproval
            AgtNo={AgtNo}
            approval={approval}
            isContAuth={isContAuth}
            onClose={() => setApproval(undefined)}
          />
        </Container>
      </AnimateIn>
    );
  }
}
